import React from "react";
import { AlertCircle, LoaderCircle } from "lucide-react";
import { ClinicianPortalApiError } from "../api/clinicianPortalApi";

export function LoadingState({
  label = "Loading portal data",
}: {
  label?: string;
}) {
  return (
    <div className="cp-state" role="status" aria-live="polite">
      <LoaderCircle className="cp-spin" aria-hidden="true" />
      <p>{label}</p>
    </div>
  );
}

export function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="cp-state">
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
  );
}

function safeErrorMessage(error: unknown): string {
  if (!(error instanceof ClinicianPortalApiError)) {
    return "Portal data could not be loaded.";
  }
  switch (error.code) {
    case "NOT_CONFIGURED":
      return "The Clinician Portal API endpoint is not configured for this environment.";
    case "NOT_AUTHORIZED":
    case "NOT_FOUND":
      return "This portal record is unavailable.";
    case "ADMIN_REQUIRED":
      return "Administrator access is required to view this page.";
    case "INVALID_REQUEST":
    case "INVALID_CLIENT_CREATE_REQUEST":
    case "INVALID_CLIENT_UPDATE_REQUEST":
    case "REQUIRED_FIELD_MISSING":
    case "INVALID_LOV_VALUE":
    case "DISABLED_LOV_VALUE":
    case "INVALID_FIELD_TYPE":
    case "INVALID_INTEGER":
    case "VALUE_BELOW_MINIMUM":
    case "VALUE_ABOVE_MAXIMUM":
    case "INVALID_HIGH_SUDS_THRESHOLD":
    case "INVALID_POINTS":
    case "INVALID_COINS":
    case "NO_CLIENT_FIELDS_PROVIDED":
    case "FIELD_NOT_EDITABLE":
    case "CLINIC_NOT_AVAILABLE":
    case "INVALID_NICKNAME":
    case "INVALID_STATUS":
    case "INVALID_SUDS_INPUT_STYLE":
    case "INVALID_COPING_STRATEGIES":
    case "INVALID_FUN_ACTIVITIES":
    case "INVALID_PATIENT_UPDATE":
    case "INVALID_DATA_REQUEST":
    case "INVALID_RANK_LEVEL_ID":
    case "RANK_LEVEL_NOT_FOUND":
    case "RANK_LEVEL_DISABLED":
    case "INVALID_THRESHOLD_POINTS":
    case "NO_RANK_LEVEL_RULE_FIELDS_PROVIDED":
    case "DUPLICATE_CLIENT_RANK_LEVEL_RULE":
    case "INVALID_COPING_SKILL_ID":
    case "COPING_SKILL_NOT_FOUND":
    case "INVALID_COPING_STRATEGY_OPTION_ID":
    case "COPING_STRATEGY_OPTION_NOT_FOUND":
    case "COPING_STRATEGY_OPTION_DISABLED":
    case "INVALID_GUIDE_STEPS":
    case "NO_COPING_SKILL_FIELDS_PROVIDED":
    case "DUPLICATE_COPING_SKILL":
    case "DUPLICATE_CLIENT_COPING_SKILL":
    case "DUPLICATE_LOV_OPTION":
    case "INVALID_CLINIC_NAME":
    case "THERAPIST_REQUIRED":
    case "THERAPIST_NOT_FOUND":
    case "DUPLICATE_CLINIC":
    case "DUPLICATE_THERAPIST":
    case "INVALID_THERAPIST_NAME":
    case "INVALID_EMAIL":
    case "INVALID_ROLE":
    case "CLINIC_NOT_FOUND":
      return "The portal request is incomplete.";
    case "NETWORK_ERROR":
    case "UPSTREAM_ERROR":
      return "Portal data is temporarily unavailable.";
    case "INVALID_RESPONSE":
      return "The portal returned an unexpected response.";
  }
}

export function ErrorState({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}) {
  return (
    <div className="cp-state cp-state--error" role="alert">
      <AlertCircle aria-hidden="true" />
      <h2>Unable to load this page</h2>
      <p>{safeErrorMessage(error)}</p>
      {onRetry ? (
        <button
          type="button"
          className="cp-button cp-button--secondary"
          onClick={onRetry}
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}
